import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, UserCircle } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import VerifiedBadge from "@/components/VerifiedBadge";
import { fileUrl } from "@/components/ImageUpload";
import api from "@/lib/api";

export default function FindPlayers() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return undefined; }
    setBusy(true);
    const t = setTimeout(() => api.get("/ratings/search", { params: { q } }).then(({ data }) => setRows(data)).catch(() => setRows([])).finally(() => setBusy(false)), 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <AppHeader title="Find & Rate Players" />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="font-display font-bold text-2xl">Find a player to rate</h1>
        <p className="text-sm mt-1 mb-5" style={{ color: "var(--text-2)" }}>Search by name, email or phone number. Contact details are never shown. Players from your events count as Event Participants (30%); others as Acquaintances (20%).</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-c" />
          <input data-testid="find-players-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email or phone number"
            className="w-full rounded-xl bg-surface glow-border pl-9 pr-3 py-3 text-sm outline-none" style={{ color: "var(--text)" }} />
          {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-primary-c" />}
        </div>
        <div className="mt-4 space-y-2" data-testid="find-players-results">
          {rows && rows.length === 0 && <p className="text-sm text-center py-8" style={{ color: "var(--text-2)" }} data-testid="find-players-empty">No players found.</p>}
          {(rows || []).map((r) => {
            const photo = r.photo ? fileUrl(r.photo) : r.picture;
            return (
              <div key={r.user_id} className="rounded-2xl glow-border p-3 flex items-center gap-3 flex-wrap" style={{ background: "var(--surface)" }} data-testid={`find-player-${r.user_id}`}>
                {photo ? <img src={photo} alt="" className="w-10 h-10 rounded-full object-cover" /> : <UserCircle className="w-10 h-10 text-primary-c" />}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold flex items-center gap-1.5 truncate">{r.name}<VerifiedBadge verified={r.verified} /></div>
                  <div className="text-[11px] mono" style={{ color: "var(--text-2)" }}>{r.player_code}{r.club ? ` · ${r.club}` : ""}{r.location ? ` · ${r.location}` : ""}</div>
                </div>
                <span className="text-[10px] mono uppercase rounded-full px-2 py-0.5 glass" data-testid={`find-player-weight-${r.user_id}`}>{r.shared_event ? "Same event · 30%" : "Acquaintance · 20%"}</span>
                <button data-testid={`find-player-rate-${r.user_id}`} onClick={() => nav(`/rate/${r.user_id}`)} className="btn-glow rounded-full px-4 py-1.5 text-xs font-bold">Rate</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
